import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";
import { Hash, ShieldCheck, Search, Users, Crown, CheckCircle2, Clock, X } from "lucide-react";

/**
 * Admin-only Master VC Registry.
 *
 * Strictly gated by backend `require_admin`. The full attribution chain
 * (every member's VC# + who introduced them + downstream count + KYC/
 * Accredited status) is never exposed to regular users.
 */
const VCRegistryPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accreditedOnly, setAccreditedOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [drilldown, setDrilldown] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vc-registry", { params: { accredited_only: accreditedOnly } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load VC registry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [accreditedOnly]);

  const openDrilldown = async (vc) => {
    if (!vc) return;
    try {
      const r = await api.get(`/admin/vc-registry/${encodeURIComponent(vc)}`);
      setDrilldown(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load VC# details");
    }
  };

  const filtered = (data?.rows || []).filter((r) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return [
      r.referral_token, r.email, r.full_name, r.referred_by, r.introducer_email,
    ].filter(Boolean).some((s) => s.toLowerCase().includes(q));
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl space-y-6" data-testid="vc-registry-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={14} className="text-blue-600" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-blue-700 font-semibold">
                Admin · Master Attribution
              </p>
            </div>
            <h1 className="text-3xl font-serif text-slate-900" data-testid="vc-registry-title">
              EchoSense Master VC# Registry
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Every member, their VC#, their introducer, and downstream count. Admin-only view —
              never exposed to regular users.
            </p>
          </div>
          <Card className="bg-blue-50 border-blue-200" data-testid="corp-vc-card">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
                <Crown size={11} /> Corporate VC#
              </p>
              <p className="font-mono text-lg font-semibold text-slate-900 mt-1" data-testid="corp-vc-value">
                {data?.corp_vc || "—"}
              </p>
              <p className="text-[10px] text-slate-600 mt-1">
                Reserved for founders & inner circle.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Toolbar */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by VC#, name, email, introducer…"
                className="pl-9"
                data-testid="vc-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="acc-only"
                checked={accreditedOnly}
                onCheckedChange={setAccreditedOnly}
                data-testid="vc-accredited-only-toggle"
              />
              <Label htmlFor="acc-only" className="text-sm text-slate-700 cursor-pointer">
                Master list (KYC + Accredited only)
              </Label>
            </div>
            <Badge className="bg-slate-100 text-slate-700 border-slate-200 border" data-testid="vc-row-count">
              <Users size={11} className="mr-1" />
              {filtered.length} of {data?.count ?? 0} members
            </Badge>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vc-registry-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                  <th className="text-left px-4 py-3 font-semibold">VC#</th>
                  <th className="text-left px-4 py-3 font-semibold">Member</th>
                  <th className="text-left px-4 py-3 font-semibold">Introduced By</th>
                  <th className="text-left px-4 py-3 font-semibold">KYC</th>
                  <th className="text-left px-4 py-3 font-semibold">Accredited</th>
                  <th className="text-left px-4 py-3 font-semibold">Downstream</th>
                  <th className="text-left px-4 py-3 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`vc-skeleton-${i}`}><td colSpan={7} className="px-4 py-3">
                      <div className="h-4 bg-slate-100 rounded animate-pulse" />
                    </td></tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="vc-registry-empty">
                    No members match this filter.
                  </td></tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`vc-row-${r.referral_token}`}>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => openDrilldown(r.referral_token)}
                          className="font-mono text-blue-700 hover:text-blue-900 font-semibold"
                          data-testid={`vc-drilldown-${r.referral_token}`}
                        >
                          {r.referral_token}
                        </button>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-slate-900 font-medium">{r.full_name || "—"}</p>
                        <p className="text-[11px] text-slate-500">{r.email}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        {r.referred_by_corp ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]">
                            <Crown size={10} className="mr-1" /> Corporate
                          </Badge>
                        ) : (
                          <button
                            onClick={() => openDrilldown(r.referred_by)}
                            className="font-mono text-xs text-slate-700 hover:text-blue-700"
                          >
                            {r.referred_by || "—"}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge value={r.kyc_status} />
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge value={r.accredited_status} />
                      </td>
                      <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">
                        {r.downstream_count ?? 0}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs">
                        {r.created_at?.slice(0, 10) || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Drilldown panel */}
      {drilldown && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 z-50"
          onClick={() => setDrilldown(null)}
          data-testid="vc-drilldown-overlay"
        >
          <Card
            className="bg-white border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="vc-drilldown-panel"
          >
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold flex items-center gap-1.5">
                    <Hash size={11} /> VC# Lookup
                  </p>
                  <p className="font-mono text-lg font-semibold text-slate-900 mt-1" data-testid="drilldown-vc">
                    {drilldown.owner.vc}
                  </p>
                  <p className="text-sm text-slate-700">
                    {drilldown.owner.full_name}{" "}
                    {drilldown.owner.is_corp && <Badge className="ml-1 bg-blue-50 text-blue-700 border-blue-200 border text-[10px]"><Crown size={10} className="mr-1" /> Corporate</Badge>}
                  </p>
                  <p className="text-xs text-slate-500">{drilldown.owner.email}</p>
                </div>
                <button
                  onClick={() => setDrilldown(null)}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="drilldown-close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                  Downstream ({drilldown.downstream_count}) — members introduced by this VC#
                </p>
                {drilldown.downstream.length === 0 ? (
                  <p className="text-sm text-slate-500 italic" data-testid="drilldown-empty">No downstream members yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {drilldown.downstream.map((d) => (
                      <li key={d.id} className="flex items-center justify-between p-2 rounded border border-slate-100 text-sm">
                        <div className="min-w-0">
                          <p className="text-slate-900 font-medium truncate">{d.full_name}</p>
                          <p className="text-[11px] text-slate-500 truncate">{d.email}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-xs text-slate-700">{d.referral_token}</span>
                          <StatusBadge value={d.kyc_status} compact />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
};

const StatusBadge = ({ value, compact }) => {
  const v = (value || "").toLowerCase();
  const ok        = v === "approved" || v === "verified";
  const inReview  = v === "in_review" || v === "pending";
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 border"
    : inReview
      ? "bg-amber-50 text-amber-700 border-amber-200 border"
      : "bg-slate-50 text-slate-600 border-slate-200 border";
  const Icon = ok ? CheckCircle2 : inReview ? Clock : null;
  return (
    <Badge className={`${cls} text-[10px]`}>
      {Icon && <Icon size={compact ? 9 : 10} className="mr-1" />}
      {value || "—"}
    </Badge>
  );
};

export default VCRegistryPage;
